import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { REACT_APP_API_URL } from "../../../../env";
import { showCmsErrorAlert, showCmsSuccessAlert } from "../../../../Common/cmsAlerts";

const API_BASE = REACT_APP_API_URL.replace(/\/$/, "");

const defaultForm = {
  intBannerID: 0,
  bannerType: "",
  redirectPath: "",
  buttonActionType: "",
  buttonText: "",
  buttonColor: "",
  buttonSizeDesktop: "",
  buttonSizeMobile: "",
  buttonBottomDesktop: "",
  buttonBottomMobile: "",
  isActive: true,
  imagePath: "",
};

const assetUrl = (assetPath) => {
  if (!assetPath) return "";
  if (/^(https?:|data:|blob:)/i.test(assetPath)) return assetPath;
  if (assetPath.startsWith("/homeBannerUploads/")) return `${API_BASE}${assetPath}`;
  if (assetPath.startsWith("/videoUploads/")) return `${API_BASE}${assetPath}`;
  return "";
};

const getDefaultRedirect = (bannerType) => (bannerType === "tour" ? "/tours" : "/events");
const isValidExternalUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());
const getDefaultButtonActionType = (redirectPath) =>
  isValidExternalUrl(redirectPath) ? "external" : "internal";

const HomepageBannerManager = () => {
  const [form, setForm] = useState(defaultForm);
  const [bannerFile, setBannerFile] = useState(null);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadBanners = useCallback(async (silent = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${REACT_APP_API_URL}api/dashboard/admin/home-banners`);
      setBanners(res.data.data || []);
      return true;
    } catch (error) {
      if (!silent) showCmsErrorAlert(error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const resetForm = () => {
    setForm(defaultForm);
    setBannerFile(null);
  };

  const sortedBanners = useMemo(
    () => [...banners].sort((a, b) => b.intBannerID - a.intBannerID),
    [banners]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!bannerFile && !form.imagePath) {
      alert("Please upload a banner image.");
      return;
    }

    if (!String(form.redirectPath || "").trim()) {
      alert("Please enter the redirect path.");
      return;
    }

    if (form.buttonActionType === "external" && !isValidExternalUrl(form.redirectPath)) {
      alert("External URLs must start with http:// or https://");
      return;
    }

    setSaving(true);

    const formData = new FormData();
    if (form.intBannerID) formData.append("intBannerID", String(form.intBannerID));
    formData.append("bannerType", form.bannerType);
    formData.append("redirectPath", form.redirectPath);
    formData.append("buttonActionType", form.buttonActionType);
    formData.append("buttonText", form.buttonText);
    formData.append("buttonColor", form.buttonColor);
    formData.append("buttonSizeDesktop", String(form.buttonSizeDesktop ?? ""));
    formData.append("buttonSizeMobile", String(form.buttonSizeMobile ?? ""));
    formData.append("buttonBottomDesktop", String(form.buttonBottomDesktop ?? ""));
    formData.append("buttonBottomMobile", String(form.buttonBottomMobile ?? ""));
    formData.append("isActive", form.isActive ? "1" : "0");
    formData.append("imagePath", form.imagePath || "");
    if (bannerFile) formData.append("bannerImage", bannerFile);

    try {
      await axios.post(`${REACT_APP_API_URL}api/dashboard/admin/home-banners`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      resetForm();
      if (await loadBanners(true)) showCmsSuccessAlert();
    } catch (error) {
      showCmsErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setForm({
      intBannerID: banner.intBannerID,
      bannerType: banner.bannerType || "event",
      redirectPath: banner.redirectPath || getDefaultRedirect(banner.bannerType),
      buttonActionType:
        banner.buttonActionType ||
        getDefaultButtonActionType(banner.redirectPath || getDefaultRedirect(banner.bannerType)),
      buttonText: banner.buttonText || (banner.bannerType === "tour" ? "Enquire Now" : "Register Now"),
      buttonColor: banner.buttonColor || "",
      buttonSizeDesktop: banner.buttonSizeDesktop ?? "",
      buttonSizeMobile: banner.buttonSizeMobile ?? "",
      buttonBottomDesktop: banner.buttonBottomDesktop ?? "",
      buttonBottomMobile: banner.buttonBottomMobile ?? "",
      isActive: banner.isActive !== false,
      imagePath: banner.imagePath || "",
    });
    setBannerFile(null);
  };

  const toggleStatus = async (banner) => {
    try {
      await axios.put(
        `${REACT_APP_API_URL}api/dashboard/admin/home-banners/${banner.intBannerID}/status`,
        { isActive: banner.isActive ? 0 : 1 }
      );
      if (await loadBanners(true)) showCmsSuccessAlert();
    } catch (error) {
      showCmsErrorAlert(error);
    }
  };

  const deleteBanner = async (banner) => {
    if (!window.confirm("Delete this home banner?")) return;

    try {
      await axios.delete(`${REACT_APP_API_URL}api/dashboard/admin/home-banners/${banner.intBannerID}`);
      if (form.intBannerID === banner.intBannerID) resetForm();
      if (await loadBanners(true)) showCmsSuccessAlert();
    } catch (error) {
      showCmsErrorAlert(error);
    }
  };

  return (
    <div className="card shadow m-4">
      <div className="card-header text-white fw-bold" style={{ background: "rgb(108 117 125)" }}>
        Homepage Banner
      </div>

      <div className="card-body">
        <div className="d-flex flex-column gap-4">
          <div>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="fw-bold form-label">Banner Image</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(event) => setBannerFile(event.target.files?.[0] || null)}
                />
                <div className="form-text">Recommended size: 1600x900 px</div>
              </div>

              {!bannerFile && form.imagePath && (
                <div className="mb-3">
                  <img
                    src={assetUrl(form.imagePath)}
                    alt="Home banner preview"
                    className="img-fluid rounded border"
                    style={{
                      maxHeight: 220,
                      maxWidth: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </div>
              )}

              <div className="row g-3 mb-3">

                {/* Banner Type */}
                <div className="col-lg-4">
                  <label className="fw-bold form-label">Banner Type</label>

                  <select
                    className="form-select"
                    value={form.bannerType}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        bannerType: event.target.value,
                        redirectPath:
                          current.redirectPath === getDefaultRedirect(current.bannerType)
                            ? getDefaultRedirect(event.target.value)
                            : current.redirectPath,
                        buttonText:
                          current.buttonText ===
                            (current.bannerType === "tour"
                              ? "Enquire Now"
                              : "Register Now")
                            ? event.target.value === "tour"
                              ? "Enquire Now"
                              : "Register Now"
                            : current.buttonText,
                      }))
                    }
                  >
                    <option value="event">Event</option>
                    <option value="tour">Tour</option>
                  </select>
                </div>

                {/* Button Action Type */}
                <div className="col-lg-4">
                  <label className="fw-bold form-label">
                    Button Action Type
                  </label>

                  <select
                    className="form-select"
                    value={form.buttonActionType}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        buttonActionType: event.target.value,
                      }))
                    }
                  >
                    <option value="internal">Internal</option>
                    <option value="external">External</option>
                  </select>
                </div>

                {/* Redirect Path */}
                <div className="col-lg-4">
                  <label className="fw-bold form-label">
                    Redirect Path (URL)
                  </label>

                  <input
                    className="form-control"
                    value={form.redirectPath}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        redirectPath: event.target.value,
                      }))
                    }
                    placeholder={
                      form.buttonActionType === "external"
                        ? "https://example.com"
                        : "/events or /tours"
                    }
                  />
                </div>

              </div>

              <div className="row g-4 mb-3">

                {/* Column 1 */}
                <div className="col-lg-4">
                  <div className="mb-3">
                    <label className="fw-bold form-label">Button Text</label>

                    <input
                      className="form-control"
                      value={form.buttonText}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          buttonText: event.target.value,
                        }))
                      }
                      placeholder="Example: Register Now"
                    />
                  </div>

                  <div>
                    <label className="fw-bold form-label">Button Color Code</label>

                    <input
                      className="form-control"
                      value={form.buttonColor}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          buttonColor: event.target.value,
                        }))
                      }
                      placeholder="Example: #ff5722"
                    />
                  </div>
                </div>

                {/* Column 2 */}
                <div className="col-lg-4">
                  <div className="mb-3">
                    <label className="fw-bold form-label">
                      Button Size (Desktop)
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={form.buttonSizeDesktop}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          buttonSizeDesktop: event.target.value,
                        }))
                      }
                      placeholder="Example: 10"
                    />
                  </div>

                  <div>
                    <label className="fw-bold form-label">
                      Button Size (Mobile)
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={form.buttonSizeMobile}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          buttonSizeMobile: event.target.value,
                        }))
                      }
                      placeholder="Example: 8"
                    />
                  </div>
                </div>

                {/* Column 3 */}
                <div className="col-lg-4">
                  <div className="mb-3">
                    <label className="fw-bold form-label">
                      Button Bottom Px (Desktop)
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={form.buttonBottomDesktop}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          buttonBottomDesktop: event.target.value,
                        }))
                      }
                      placeholder="Example: 15"
                    />
                  </div>

                  <div>
                    <label className="fw-bold form-label">
                      Button Bottom Px (Mobile)
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={form.buttonBottomMobile}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          buttonBottomMobile: event.target.value,
                        }))
                      }
                      placeholder="Example: 10"
                    />
                  </div>
                </div>

              </div>

              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="home-banner-active"
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, isActive: event.target.checked }))
                  }
                />
                <label htmlFor="home-banner-active" className="form-check-label">
                  Show this banner on homepage
                </label>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-success" disabled={saving}>
                  {saving ? "Saving..." : form.intBannerID ? "Update Banner" : "Save Banner"}
                </button>
                {form.intBannerID > 0 && (
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>

          <div>
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Preview</th>
                    <th>Type</th>
                    <th>Redirect</th>
                    <th>Status</th>
                    <th width="220">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && sortedBanners.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No home banners added yet.
                      </td>
                    </tr>
                  )}

                  {sortedBanners.map((banner) => (
                    <tr key={banner.intBannerID}>
                      <td>{banner.intBannerID}</td>
                      <td>
                        {banner.imagePath ? (
                          <img
                            src={assetUrl(banner.imagePath)}
                            alt={banner.bannerType}
                            className="rounded border"
                            style={{ width: 120, height: 54, objectFit: "cover" }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="text-capitalize">{banner.bannerType}</td>
                      <td>{banner.redirectPath}</td>
                      <td>
                        <span className={`badge ${banner.isActive ? "bg-success" : "bg-secondary"}`}>
                          {banner.isActive ? "Active" : "Hidden"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEdit(banner)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => toggleStatus(banner)}
                          >
                            {banner.isActive ? "Hide" : "Show"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteBanner(banner)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageBannerManager;
